import { supabase } from '@/lib/supabase';

export type ReportFormat = 'pdf' | 'excel';
export type ReportChannel = 'download' | 'email' | 'whatsapp';

export interface StoreReportLine {
  product_id: string;
  sku: string | null;
  product_name: string;
  qty: number;
  unit_price: number | null;
}

export interface StoreReport {
  access: 'business' | 'agent' | 'client';
  company_name: string | null;
  logo_url: string | null;
  client_id: string;
  client_name: string;
  client_email: string | null;
  client_phone: string | null;
  warehouse_name: string | null;
  pricelist_name: string | null;
  as_of: string;
  vat_rate: number;
  lines: StoreReportLine[];
}

export interface StoreReportTotals {
  qty: number;
  beforeVat: number;
  vat: number;
  inclVat: number;
  unpricedCount: number;
}

export async function fetchWhatsAppAvailable(): Promise<boolean> {
  const { data, error } = await supabase.functions.invoke('send-consignment-report', {
    body: { action: 'status' },
  });
  if (error) return false;
  return (data as { whatsapp?: boolean } | null)?.whatsapp === true;
}

export function todayIso(): string {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
}

export function formatReportDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

export function fmtMoney(n: number): string {
  return n.toLocaleString('he-IL', { style: 'currency', currency: 'ILS', minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmtQty(n: number): string {
  return Number.isInteger(n) ? n.toLocaleString('he-IL') : parseFloat(n.toFixed(3)).toLocaleString('he-IL');
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export function lineValues(line: StoreReportLine, vatRate: number) {
  if (line.unit_price == null) return null;
  const beforeVat = round2(line.unit_price * line.qty);
  return { beforeVat, inclVat: round2(beforeVat * (1 + vatRate / 100)) };
}

export function computeTotals(report: StoreReport): StoreReportTotals {
  let qty = 0;
  let beforeVat = 0;
  let unpricedCount = 0;
  for (const l of report.lines) {
    qty += l.qty;
    const v = lineValues(l, report.vat_rate);
    if (v) beforeVat += v.beforeVat;
    else unpricedCount += 1;
  }
  beforeVat = round2(beforeVat);
  const vat = round2(beforeVat * report.vat_rate / 100);
  return { qty, beforeVat, vat, inclVat: round2(beforeVat + vat), unpricedCount };
}

function isStoreReport(v: unknown): v is StoreReport {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  return typeof r.client_id === 'string' && Array.isArray(r.lines) && typeof r.as_of === 'string';
}

export async function fetchStoreReport(companyId: string, clientId: string, asOf: string): Promise<StoreReport> {
  const { data, error } = await supabase.rpc('get_consignment_store_report', {
    p_company_id: companyId,
    p_client_id: clientId,
    p_as_of: asOf,
  });
  if (error) {
    if (error.message.includes('no consignment warehouse')) throw new Error('לחנות זו לא מוגדר מחסן קונסיגנציה');
    if (error.code === '42501') throw new Error('אין לך הרשאה לצפות בדוח של חנות זו');
    throw new Error('שגיאה בטעינת הדוח');
  }
  if (!isStoreReport(data)) throw new Error('התקבל דוח לא תקין');
  return {
    ...data,
    vat_rate: Number(data.vat_rate) || 0,
    lines: data.lines.map((l) => ({
      ...l,
      qty: Number(l.qty),
      unit_price: l.unit_price == null ? null : Number(l.unit_price),
    })),
  };
}

export function reportFileName(report: StoreReport, format: ReportFormat): string {
  const safe = report.client_name.replace(/[\\/:*?"<>|\s]+/g, '_');
  return `יתרות_מלאי_${safe}_${report.as_of}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
}

export async function buildExcelBlob(report: StoreReport): Promise<Blob> {
  const XLSX = await import('xlsx');
  const totals = computeTotals(report);
  const rows: (string | number | null)[][] = [
    [report.company_name ?? ''],
    ['דוח יתרות מלאי קונסיגנציה'],
    ['חנות', report.client_name],
    ['מחסן', report.warehouse_name ?? ''],
    ['נכון לתאריך', formatReportDate(report.as_of)],
    [`מע"מ`, `${report.vat_rate}%`],
    [],
    ['מק"ט', 'שם מוצר', 'כמות', 'מחיר יחידה', 'שווי לפני מע"מ', 'שווי כולל מע"מ'],
    ...report.lines.map((l) => {
      const v = lineValues(l, report.vat_rate);
      return [l.sku ?? '', l.product_name, l.qty, l.unit_price ?? 'ללא מחיר', v?.beforeVat ?? null, v?.inclVat ?? null];
    }),
    [],
    ['סה"כ', '', totals.qty, '', totals.beforeVat, totals.inclVat],
    [`סה"כ מע"מ`, '', '', '', totals.vat],
  ];
  if (totals.unpricedCount > 0) {
    rows.push([`${totals.unpricedCount} פריטים ללא מחיר אינם כלולים בשווי`]);
  }
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 16 }, { wch: 36 }, { wch: 10 }, { wch: 14 }, { wch: 16 }, { wch: 16 }];
  ws['!views'] = [{ RTL: true }] as never;
  const wb = XLSX.utils.book_new();
  wb.Workbook = { Views: [{ RTL: true }] };
  XLSX.utils.book_append_sheet(wb, ws, 'יתרות');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

export async function buildPdfBlob(el: HTMLElement): Promise<Blob> {
  const { renderElementToPdfBlob } = await import('@/lib/pdfExport');
  return renderElementToPdfBlob(el, { orientation: 'portrait' });
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 8192) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
  }
  return btoa(binary);
}

export async function logReportDownload(companyId: string, clientId: string, format: ReportFormat, asOf: string) {
  const { error } = await supabase.rpc('log_consignment_report_download', {
    p_company_id: companyId,
    p_client_id: clientId,
    p_format: format,
    p_as_of: asOf,
  });
  if (error) console.error('Failed to log report download:', error);
}

export async function saveClientEmail(companyId: string, clientId: string, email: string) {
  const { error } = await supabase.rpc('set_consignment_client_email', {
    p_company_id: companyId,
    p_client_id: clientId,
    p_email: email,
  });
  if (error) throw new Error('לא ניתן היה לשמור את המייל בכרטיס החנות');
}

export async function sendStoreReport(params: {
  companyId: string;
  report: StoreReport;
  format: ReportFormat;
  channel: Exclude<ReportChannel, 'download'>;
  recipient: string;
  blob: Blob;
}): Promise<void> {
  const { companyId, report, format, channel, recipient, blob } = params;
  const { data, error } = await supabase.functions.invoke('send-consignment-report', {
    body: {
      company_id: companyId,
      client_id: report.client_id,
      as_of: report.as_of,
      format,
      channel,
      recipient,
      filename: reportFileName(report, format),
      file_base64: await blobToBase64(blob),
      client_name: report.client_name,
      company_name: report.company_name,
    },
  });
  if (error) {
    let message = 'השליחה נכשלה';
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const body = await ctx.json();
        if (body?.error === 'whatsapp_not_configured') message = 'שליחה בווטסאפ עדיין לא הופעלה';
        else if (body?.error === 'forbidden') message = 'אין לך הרשאה לשלוח דוח לחנות זו';
      } catch {
        /* keep generic message */
      }
    }
    throw new Error(message);
  }
  if (!data || (data as { ok?: boolean }).ok !== true) throw new Error('השליחה נכשלה');
}

export interface LastSent {
  last_sent_at: string;
  last_channel: string;
}

export function fmtSentDate(iso: string): string {
  return new Date(iso).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export async function fetchLastSent(companyId: string): Promise<Record<string, LastSent>> {
  const { data, error } = await supabase.rpc('get_consignment_report_last_sent', { p_company_id: companyId });
  if (error) throw error;
  const out: Record<string, LastSent> = {};
  for (const r of (data ?? []) as { client_id: string; last_sent_at: string; last_channel: string }[]) {
    out[r.client_id] = { last_sent_at: r.last_sent_at, last_channel: r.last_channel };
  }
  return out;
}
